import React from 'react';
import { useTranslation } from 'react-i18next';
import { Meta } from '../components/Meta';
import { Heart, Users, Leaf, Globe, Shield, Sparkles } from 'lucide-react';

export function AboutUs() {
  const { t } = useTranslation();
  return (
    <div>
      <Meta 
        title={t('aboutUs.metaTitle')}
        description={t('aboutUs.metaDesc')}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-[#F3F7EE] to-white py-24">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-6">
                {t('aboutUs.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {t('aboutUs.subtitle')}
              </p>
            </div>
            <div className="mt-12 lg:mt-0 flex justify-center lg:justify-end">
              <img 
                src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/OwnersMindfulFamily.png"
                alt="Mary and Rik - Founders of Mindful Family"
                className="blob-shape w-full max-w-md lg:max-w-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <p className="text-2xl text-content/80 mb-8">{t('aboutUs.intro')} 😊</p>
          
          <div className="space-y-6 text-lg text-content/80">
            <p>
              {t('aboutUs.story1')}
            </p>
            
            <p>
              {t('aboutUs.story2')}
            </p>

            <p>
              {t('aboutUs.story3')}
            </p>
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="py-24 bg-[#F3F7EE]">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-3xl font-gelica font-bold text-content mb-6">{t('aboutUs.missionTitle')}</h2>
              <p className="text-lg text-content/80">
                {t('aboutUs.missionText')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-3xl font-gelica font-bold text-content mb-6">{t('aboutUs.visionTitle')}</h2>
              <p className="text-lg text-content/80">
                {t('aboutUs.visionText')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="py-24 bg-white">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <h2 className="text-3xl font-gelica font-bold text-content text-center mb-16">{t('aboutUs.valuesTitle')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#F3F7EE] rounded-xl p-8 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="h-6 w-6 text-accent-text" />
                <h3 className="text-xl font-semibold text-content">{t('aboutUs.valueWellbeing')}</h3>
              </div>
              <p className="text-content/80">
                {t('aboutUs.valueWellbeingDesc')}
              </p>
            </div>

            <div className="bg-[#F3F7EE] rounded-xl p-8 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-4">
                <Users className="h-6 w-6 text-accent-text" />
                <h3 className="text-xl font-semibold text-content">{t('aboutUs.valueCommunity')}</h3>
              </div>
              <p className="text-content/80">
                {t('aboutUs.valueCommunityDesc')}
              </p>
            </div>

            <div className="bg-[#F3F7EE] rounded-xl p-8 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-4">
                <Leaf className="h-6 w-6 text-accent-text" />
                <h3 className="text-xl font-semibold text-content">{t('aboutUs.valueMindfulness')}</h3>
              </div>
              <p className="text-content/80">
                {t('aboutUs.valueMindfulnessDesc')}
              </p>
            </div>

            <div className="bg-[#F3F7EE] rounded-xl p-8 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-4">
                <Globe className="h-6 w-6 text-accent-text" />
                <h3 className="text-xl font-semibold text-content">{t('aboutUs.valueSustainability')}</h3>
              </div>
              <p className="text-content/80">
                {t('aboutUs.valueSustainabilityDesc')}
              </p>
            </div>

            <div className="bg-[#F3F7EE] rounded-xl p-8 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="h-6 w-6 text-accent-text" />
                <h3 className="text-xl font-semibold text-content">{t('aboutUs.valueEmpowerment')}</h3>
              </div>
              <p className="text-content/80">
                {t('aboutUs.valueEmpowermentDesc')}
              </p>
            </div>

            <div className="bg-[#F3F7EE] rounded-xl p-8 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-4">
                <Sparkles className="h-6 w-6 text-accent-text" />
                <h3 className="text-xl font-semibold text-content">{t('aboutUs.valueInnovation')}</h3>
              </div>
              <p className="text-content/80">
                {t('aboutUs.valueInnovationDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Closing CTA */}
      <div className="py-24 bg-gradient-to-b from-[#F3F7EE] to-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10">
          <h2 className="text-3xl font-gelica font-bold text-content mb-6">
            {t('aboutUs.joinTitle')}
          </h2>
          <p className="text-xl text-content/80 mb-8">
            {t('aboutUs.joinDesc')}
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-accent-text hover:bg-accent-text/90 transition-colors"
          >
            {t('aboutUs.createAccount')}
          </button>
        </div>
      </div>
    </div>
  );
}